/**
 * Brand / Category / Subtype for a product, each linked to the page that lists
 * more of it.
 *
 * Shared by the PDP and the product modal, which rendered the same block as
 * plain text in two places. Keeping one component means the two cannot drift.
 *
 * Each value links only when there is a real page behind it — an unmapped
 * category or a missing brand renders as plain text rather than a link to
 * nowhere. Brand URLs come from `toBrandSlug` and category URLs from
 * `categoryPath`; neither is derived inline, because both have been a source of
 * broken links when hand-rolled.
 */
import { Link } from '@remix-run/react';
import { toBrandSlug } from '~/utils/brand-slug';
import { categoryPath, subtypePath } from '~/utils/category-slug';

interface ProductTaxonomyLinksProps {
  brand?: string | null;
  category?: string | null;
  subtype?: string | null;
  /** Tailwind text colour for the "Brand:" / "Category:" labels. */
  labelClassName: string;
  /**
   * Called when a link is followed. The modal passes its close handler: it
   * closes itself on pathname change, which a subtype link from the matching
   * category page does not trigger, since only the query string moves.
   */
  onNavigate?: () => void;
}

/** "LIVE ROSIN" → "Live Rosin". Matches how these values already read. */
function titleCase(value: string): string {
  return value
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

const valueClass = 'font-helvetica-neue text-lg leading-[21px] font-normal';
/*
 * Underlined always, not just on hover. Colour alone cannot be the only signal
 * that something is a link (WCAG 1.4.1), and these sit inline among plain text
 * where there is nothing else to distinguish them.
 */
const linkClass = `${valueClass} rounded text-[#264B9A] underline hover:no-underline focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none`;

function Row({
  label,
  value,
  to,
  labelClassName,
  onNavigate,
}: {
  label: string;
  value?: string | null;
  to: string | null;
  labelClassName: string;
  onNavigate?: () => void;
}) {
  const raw = value?.trim();
  const text = raw ? titleCase(raw) : 'N/A';
  return (
    <div className="flex items-start gap-1">
      <span className={`font-helvetica-neue text-lg leading-[21px] font-normal ${labelClassName}`}>
        {label}:
      </span>
      {to ? (
        <Link
          to={to}
          onClick={onNavigate}
          className={linkClass}
          aria-label={`Shop more ${label.toLowerCase()}: ${text}`}
        >
          {text}
        </Link>
      ) : (
        <span className={`${valueClass} text-black`}>{text}</span>
      )}
    </div>
  );
}

export default function ProductTaxonomyLinks({
  brand,
  category,
  subtype,
  labelClassName,
  onNavigate,
}: ProductTaxonomyLinksProps) {
  return (
    <div className="flex w-full flex-row flex-wrap items-start gap-3 lg:flex-col lg:gap-3">
      <Row
        label="Brand"
        value={brand}
        to={brand?.trim() ? `/brand/${toBrandSlug(brand.trim())}` : null}
        labelClassName={labelClassName}
        onNavigate={onNavigate}
      />
      <Row
        label="Category"
        value={category}
        to={categoryPath(category)}
        labelClassName={labelClassName}
        onNavigate={onNavigate}
      />
      <Row
        label="Subtype"
        value={subtype}
        to={subtypePath(category, subtype)}
        labelClassName={labelClassName}
        onNavigate={onNavigate}
      />
    </div>
  );
}
